import { priorityKind } from '@/lib/cobalt-helpers';
// Color classes for Autotask ticket status IDs. Tailwind requires the full
// class strings to appear literally in source — keep these as plain strings.
//
// IDs from /api/picklists status (Synfo Autotask instance):
//   1  New
//   5  Complete
//   7  Waiting Customer
//   8  In Progress
//   10 Assigned
//   19 Customer Note Added
//   20 Waiting Vendor
//   21 Complete-Pending Customer
//   22 Waiting Parts
//   23 Escalated
//   25 Scheduled
//   26 On-Hold

const STATUS_COLORS: Record<number, string> = {
  1:  'bg-blue-100 text-blue-900 dark:bg-blue-500/20 dark:text-blue-300',          // New
  5:  'bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-300', // Complete
  7:  'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',      // Waiting Customer
  8:  'bg-sky-100 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300',              // In Progress
  10: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-500/20 dark:text-indigo-300',  // Assigned
  19: 'bg-orange-100 text-orange-900 dark:bg-orange-500/20 dark:text-orange-300',  // Customer Note Added
  20: 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',      // Waiting Vendor
  21: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300', // Complete-Pending Customer
  22: 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',      // Waiting Parts
  23: 'bg-red-100 text-red-900 dark:bg-red-500/20 dark:text-red-300',              // Escalated
  25: 'bg-purple-100 text-purple-900 dark:bg-purple-500/20 dark:text-purple-300',  // Scheduled
  26: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-500/25 dark:text-zinc-300',          // On-Hold
};

const FALLBACK = 'bg-secondary text-secondary-foreground';

export function statusColor(id: number | null | undefined): string {
  if (id == null) return FALLBACK;
  return STATUS_COLORS[id] ?? FALLBACK;
}

// Priority colours by Synfo's Autotask priority id (NOT Autotask's documented defaults):
//   4 Critical, 1 High, 2 Medium, 3 Low, 5 Very Low. Grids show priority as a dot (InlineEdit / PriorityTag).
const PRIORITY_COLORS: Record<number, string> = {
  4: 'bg-red-100 text-red-900 dark:bg-red-500/20 dark:text-red-300',
  1: 'bg-orange-100 text-orange-900 dark:bg-orange-500/20 dark:text-orange-300',
  2: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-500/20 dark:text-yellow-200',
  3: 'bg-blue-100 text-blue-900 dark:bg-blue-500/20 dark:text-blue-300',
  5: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-300',
};

export function priorityColor(id: number | null | undefined): string {
  if (id == null) return FALLBACK;
  return PRIORITY_COLORS[id] ?? FALLBACK;
}

// ── Dots (dropdowns + filter menus show a colour dot, never the full pill) ──────────────
// CSS colours, same hues as the status chips above.
const STATUS_DOTS: Record<number, string> = {
  1: '#3b82f6',  // New — blue
  5: '#10b981',  // Complete — emerald
  7: '#f59e0b',  // Waiting Customer — amber
  8: '#0ea5e9',  // In Progress — sky
  10: '#6366f1', // Assigned — indigo
  19: '#f97316', // Customer Note Added — orange
  20: '#f59e0b', // Waiting Vendor — amber
  21: '#34d399', // Complete-Pending Customer — light emerald
  22: '#f59e0b', // Waiting Parts — amber
  23: '#ef4444', // Escalated — red
  25: '#a855f7', // Scheduled — purple
  26: '#a1a1aa', // On-Hold — zinc
};

/** Ticket status id → dot colour. */
export function statusDot(id: number | null | undefined): string {
  return (id != null && STATUS_DOTS[id]) || 'hsl(var(--neutral))';
}

/** Priority label → dot colour: Critical red, High orange, Medium yellow, Low blue, Very Low green. */
export function priorityDot(label: string | null | undefined): string {
  return `hsl(var(--pri-${priorityKind(label)}))`;
}

/** Opportunity status (Synfo: 0 Not Ready To Buy, 1 Active, 2 Lost, 3 Closed, 4 Implemented) → semantic kind. */
export const OPP_STATUS_KIND: Record<number, 'warning' | 'brand' | 'destructive' | 'success'> = {
  0: 'warning', 1: 'brand', 2: 'destructive', 3: 'success', 4: 'success',
};

/** Opportunity status id → dot colour. */
export function oppStatusDot(id: number | null | undefined): string {
  const k = id != null ? OPP_STATUS_KIND[id] : undefined;
  return k ? `hsl(var(--${k}))` : 'hsl(var(--neutral))';
}
